// Per-user settings — one DynamoDB row per setting.
//
// Settings are PRIVATE (PK = the caller's Cognito Identity ID) under SK
// `settings#<key>`, e.g. `settings#nutrition.energy_unit`. One row per setting
// keeps them independently readable/writable and easy to extend over time.

import { getItem, updateItem } from './db.js';
import { ENERGY_UNITS } from './ingredients.js';

const SETTINGS_PREFIX = 'settings#';

export const SETTING_KEYS = Object.freeze({
  ENERGY_UNIT: 'nutrition.energy_unit',
  // Daily energy target, stored in kJ (storage unit), independent of the
  // display unit. The menu generator fills each day toward this value.
  DAILY_GOAL_KJ: 'nutrition.daily_goal_kj',
  // Acceptable deviation below/above the daily goal, each as a percentage.
  // Under defaults to 0 (always at least meet the goal); over defaults to 10.
  UNDER_TOLERANCE_PCT: 'nutrition.under_tolerance_pct',
  OVER_TOLERANCE_PCT: 'nutrition.over_tolerance_pct',
  // How many days cooked leftovers (recipe-stock) keep before expiring. Applied
  // when a prepared/cooked batch deposits its surplus servings into stock.
  LEFTOVER_SHELF_LIFE_DAYS: 'nutrition.leftover_shelf_life_days',
  // The strategy pre-selected in the menu Generate dialog. Stored as a strategy
  // itemId (`strategy#…`), or null to fall back to the seeded shared default.
  DEFAULT_STRATEGY_REF: 'menu.default_strategy_ref',
  // Tag allow-/deny-lists that hide matching items EVERYWHERE the app shows a
  // list of all ingredients / recipes (and from menu generation + the picker).
  // Already-referenced items still resolve, so existing menus/stock don't break.
  IGNORE_INGREDIENT_TAGS: 'catalogue.ignore_ingredient_tags',
  IGNORE_RECIPE_TAGS: 'catalogue.ignore_recipe_tags',
  // Whether the Debug tools category is shown in the nav + home page. Off by
  // default — these are developer/insight tools, not part of the normal flow.
  SHOW_DEBUG: 'debug.show_debug',
});

const DEFAULTS = Object.freeze({
  [SETTING_KEYS.ENERGY_UNIT]: ENERGY_UNITS.KCAL,
  // 8700 kJ ≈ 2000 kcal — the conventional reference daily intake.
  [SETTING_KEYS.DAILY_GOAL_KJ]: 8700,
  [SETTING_KEYS.UNDER_TOLERANCE_PCT]: 0,
  [SETTING_KEYS.OVER_TOLERANCE_PCT]: 10,
  // Mirrors menu.js DEFAULT_LEFTOVER_DAYS (kept as a literal to avoid a
  // settings→menu module dependency); 4 days is the built-in leftover shelf life.
  [SETTING_KEYS.LEFTOVER_SHELF_LIFE_DAYS]: 4,
  // No explicit default strategy → the menu dialog uses the seeded shared one.
  [SETTING_KEYS.DEFAULT_STRATEGY_REF]: null,
  // Hide Lite n Easy ingredients by default (they dominate the catalogue); no
  // recipes hidden by default.
  [SETTING_KEYS.IGNORE_INGREDIENT_TAGS]: ['Lite n Easy'],
  [SETTING_KEYS.IGNORE_RECIPE_TAGS]: [],
  [SETTING_KEYS.SHOW_DEBUG]: false,
});

function settingId(key) {
  return `${SETTINGS_PREFIX}${key}`;
}

/**
 * Read a single setting's value, falling back to a built-in default when the
 * user has never set it. The value is stored in the row's `value` attribute.
 */
export async function getSetting(doc, userId, key) {
  const row = await getItem(doc, userId, settingId(key));
  return row?.value ?? DEFAULTS[key] ?? null;
}

/** Write a single setting's value (upsert). */
export function setSetting(doc, userId, key, value) {
  return updateItem(doc, userId, settingId(key), { value });
}

/** Convenience: the user's preferred energy unit ('cal' | 'kj'). */
export function getEnergyUnit(doc, userId) {
  return getSetting(doc, userId, SETTING_KEYS.ENERGY_UNIT);
}

/** Convenience: the daily energy goal in kJ. */
export function getDailyGoalKj(doc, userId) {
  return getSetting(doc, userId, SETTING_KEYS.DAILY_GOAL_KJ);
}

/** Convenience: allowed shortfall below the goal, as a percentage (e.g. 0). */
export function getUnderTolerancePct(doc, userId) {
  return getSetting(doc, userId, SETTING_KEYS.UNDER_TOLERANCE_PCT);
}

/** Convenience: allowed overshoot above the goal, as a percentage (e.g. 10). */
export function getOverTolerancePct(doc, userId) {
  return getSetting(doc, userId, SETTING_KEYS.OVER_TOLERANCE_PCT);
}

/** Convenience: cooked-leftover shelf life in days (e.g. 4). */
export function getLeftoverShelfLifeDays(doc, userId) {
  return getSetting(doc, userId, SETTING_KEYS.LEFTOVER_SHELF_LIFE_DAYS);
}

/** Convenience: the default menu-strategy itemId (or null to use the seeded one). */
export function getDefaultStrategyRef(doc, userId) {
  return getSetting(doc, userId, SETTING_KEYS.DEFAULT_STRATEGY_REF);
}

/** Convenience: tags whose ingredients are hidden from all catalogue lists. */
export async function getIgnoreIngredientTags(doc, userId) {
  const v = await getSetting(doc, userId, SETTING_KEYS.IGNORE_INGREDIENT_TAGS);
  return Array.isArray(v) ? v : [];
}

/** Convenience: tags whose recipes are hidden from all catalogue lists. */
export async function getIgnoreRecipeTags(doc, userId) {
  const v = await getSetting(doc, userId, SETTING_KEYS.IGNORE_RECIPE_TAGS);
  return Array.isArray(v) ? v : [];
}

/** Convenience: whether the Debug tools category is shown (defaults false). */
export async function getShowDebug(doc, userId) {
  return (await getSetting(doc, userId, SETTING_KEYS.SHOW_DEBUG)) === true;
}

/**
 * Read every settable value at once, applying built-in defaults for any the
 * user has never set. Returns a plain object keyed by friendly camelCase names
 * (the shape the settings page renders + saves).
 */
export async function getAllSettings(doc, userId) {
  const [
    energyUnit,
    dailyGoalKj,
    underTolerancePct,
    overTolerancePct,
    leftoverShelfLifeDays,
    defaultStrategyRef,
    ignoreIngredientTags,
    ignoreRecipeTags,
    showDebug,
  ] = await Promise.all([
    getSetting(doc, userId, SETTING_KEYS.ENERGY_UNIT),
    getSetting(doc, userId, SETTING_KEYS.DAILY_GOAL_KJ),
    getSetting(doc, userId, SETTING_KEYS.UNDER_TOLERANCE_PCT),
    getSetting(doc, userId, SETTING_KEYS.OVER_TOLERANCE_PCT),
    getSetting(doc, userId, SETTING_KEYS.LEFTOVER_SHELF_LIFE_DAYS),
    getSetting(doc, userId, SETTING_KEYS.DEFAULT_STRATEGY_REF),
    getIgnoreIngredientTags(doc, userId),
    getIgnoreRecipeTags(doc, userId),
    getShowDebug(doc, userId),
  ]);
  return {
    energyUnit,
    dailyGoalKj,
    underTolerancePct,
    overTolerancePct,
    leftoverShelfLifeDays,
    defaultStrategyRef,
    ignoreIngredientTags,
    ignoreRecipeTags,
    showDebug,
  };
}

/** Map from the friendly camelCase names to their storage keys. */
const FRIENDLY_TO_KEY = Object.freeze({
  energyUnit: SETTING_KEYS.ENERGY_UNIT,
  dailyGoalKj: SETTING_KEYS.DAILY_GOAL_KJ,
  underTolerancePct: SETTING_KEYS.UNDER_TOLERANCE_PCT,
  overTolerancePct: SETTING_KEYS.OVER_TOLERANCE_PCT,
  leftoverShelfLifeDays: SETTING_KEYS.LEFTOVER_SHELF_LIFE_DAYS,
  defaultStrategyRef: SETTING_KEYS.DEFAULT_STRATEGY_REF,
  ignoreIngredientTags: SETTING_KEYS.IGNORE_INGREDIENT_TAGS,
  ignoreRecipeTags: SETTING_KEYS.IGNORE_RECIPE_TAGS,
  showDebug: SETTING_KEYS.SHOW_DEBUG,
});

/**
 * Upsert one or more settings. Accepts the friendly-named shape from
 * `getAllSettings`; only the keys present in `values` are written. Returns once
 * all writes complete.
 */
export function saveSettings(doc, userId, values = {}) {
  const writes = [];
  for (const [friendly, key] of Object.entries(FRIENDLY_TO_KEY)) {
    const value = values[friendly];
    if (value !== undefined) writes.push(setSetting(doc, userId, key, value));
  }
  return Promise.all(writes);
}
